import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { downloadBlob } from "../ui";
import { KINDS, PLATFORMS, fmt, fmtDate } from "./meta";
import { RowsTable } from "./RowsTable";

const PAGE = 100;

export function RowsPanel({ run, kind, canExport, sel, onSel, onUndo, reloadKey }) {
  const [f, setF] = usePersistedState(`tracked-preview.${kind}.f`, { platform: "", reason: "", q: "" });
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const seq = useRef(0);
  const lastRun = useRef(`${kind}|${run.id}`);
  useEffect(() => {
    if (lastRun.current === `${kind}|${run.id}`) return;
    lastRun.current = `${kind}|${run.id}`;
    setSkip(0); setData(null); setF((x) => (x.reason ? { ...x, reason: "" } : x));
  }, [kind, run.id, setF]);
  useEffect(() => {
    const t = setTimeout(() => {
      const mine = ++seq.current;
      api.get(`/billing-sync/preview/runs/${run.id}/rows`, { params: { kind, ...f, skip, limit: PAGE } })
        .then(({ data: d }) => { if (mine === seq.current) setData(d); })
        .catch(() => { if (mine === seq.current) toast.error("Could not load the preview rows"); });
    }, f.q ? 250 : 0);
    return () => clearTimeout(t);
  }, [run.id, kind, f, skip, reloadKey]);
  const exportCsv = async () => {
    try { const { data: b } = await api.get(`/billing-sync/preview/runs/${run.id}/export`, { params: { kind, ...f }, responseType: "blob" }); downloadBlob(b, `tracked_items_preview_${kind}_${run.id}.csv`); }
    catch (e) { toast.error("Could not export"); }
  };
  const label = KINDS.find((k) => k.key === kind)?.label;
  const reasons = run.summary?.issues_by_reason || [];
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="bsp-rows-panel">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <h3 className="font-heading text-sm font-semibold text-slate-800" data-testid="bsp-rows-title">{label}</h3>
        {data && <span className="text-xs text-slate-500" data-testid="bsp-rows-total">{fmt(data.total)} rows</span>}
        {onSel && ["add", "move", "remove", "create_subscription"].includes(kind) && <span className="hidden text-[11px] text-slate-400 2xl:inline">· tick rows, or the double tick by a domain for all of it</span>}
        <div className="relative ml-2 w-64"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={f.q} onChange={(e) => { setSkip(0); setF({ ...f, q: e.target.value }); }} placeholder="Search domain, extension, DID, product…" className="h-9 pl-8 text-xs" data-testid="bsp-rows-search" /></div>
        <select value={f.platform} onChange={(e) => { setSkip(0); setF({ ...f, platform: e.target.value }); }} data-testid="bsp-rows-platform" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs">
          <option value="">All platforms</option>{PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</select>
        {kind === "issue" && reasons.length > 0 && (
          <select value={f.reason} onChange={(e) => { setSkip(0); setF({ ...f, reason: e.target.value }); }} data-testid="bsp-rows-reason" className="h-9 max-w-xs rounded-md border border-slate-200 bg-white px-2 text-xs">
            <option value="">All problems</option>{reasons.map((r) => <option key={r.reason} value={r.reason}>{`${r.message} (${r.count})`}</option>)}</select>)}
        {canExport && <Button size="sm" variant="outline" className="ml-auto" onClick={exportCsv} data-testid="bsp-export"><Download className="h-3.5 w-3.5" /> Export CSV</Button>}
      </div>
      {kind === "remove" && (
        <div className="border-b border-rose-100 bg-rose-50/60 px-4 py-2 text-xs text-rose-900" data-testid="bsp-remove-note">
          Removals only run on the last day of the month (next: <b>{fmtDate(run.summary?.removals_run_on)}</b>). They are listed now so you can check them ahead of time. An item counts as gone only when it is missing from every platform.
        </div>)}
      <div className="max-h-[62vh] overflow-auto">
        {!data ? <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-slate-300" />
          : data.rows.length ? <RowsTable kind={kind} rows={data.rows} sel={sel} onSel={onSel} onUndo={onUndo} />
            : <p className="p-10 text-center text-sm text-slate-500" data-testid="bsp-rows-empty">Nothing here — the scripts would not change anything of this kind.</p>}
      </div>
      {data && data.total > PAGE && (
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
          {fmt(skip + 1)}–{fmt(Math.min(skip + PAGE, data.total))} of {fmt(data.total)}
          <Button size="sm" variant="ghost" disabled={!skip} onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="bsp-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)} data-testid="bsp-page-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>)}
    </Card>
  );
}
